import type { PortalAgreement, PortalSession } from "./types";

export interface SessionAgreements {
  session: PortalSession;
  agreements: PortalAgreement[];
}

/**
 * The patient's published agreements, gathered under the session each was
 * made in.
 *
 * Sessions keep the order of the list they came in, agreements the order the
 * server sent them. A session with nothing published is left out, and so is an
 * agreement whose session is not in the list.
 */
export function groupAgreementsBySession(
  sessions: PortalSession[],
  agreements: PortalAgreement[]
): SessionAgreements[] {
  const bySession = new Map<string, PortalAgreement[]>();
  for (const agreement of agreements) {
    if (agreement.state !== "published") continue;
    const group = bySession.get(agreement.session_id) ?? [];
    group.push(agreement);
    bySession.set(agreement.session_id, group);
  }

  return sessions
    .filter((session) => bySession.has(session.id))
    .map((session) => ({ session, agreements: bySession.get(session.id) ?? [] }));
}

export interface OpenAgreement {
  agreement: PortalAgreement;
  session: PortalSession;
}

const isOpen = (agreement: PortalAgreement) =>
  agreement.status === null || agreement.status === "pending" || agreement.status === "in_progress";

/**
 * What the patient still has to do: agreements nobody marked done or skipped,
 * the latest session's first and each session's in the order they were made.
 */
export function openAgreements(groups: SessionAgreements[]): OpenAgreement[] {
  return [...groups]
    .sort((a, b) => b.session.date.localeCompare(a.session.date))
    .flatMap(({ session, agreements }) =>
      [...agreements]
        .sort((a, b) => a.order - b.order)
        .filter(isOpen)
        .map((agreement) => ({ agreement, session }))
    );
}

/** How many of the shared agreements are done, out of all of them. */
export function agreementProgress(groups: SessionAgreements[]): { completed: number; total: number } {
  const all = groups.flatMap((group) => group.agreements);
  return {
    completed: all.filter((agreement) => agreement.status === "completed").length,
    total: all.length,
  };
}
